/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from "@emotion/react";
import { Column } from "../../../layout/flexbox";
import { COLUMN_SPACING } from "../../../forms/styles";
import { EditableLiftAccessRules } from "./models";
import { EditableEffectiveDateRangeAccessRule } from "../effectiveDateRange/effectiveDateRange";
import { EffectiveDateRangeAccessRuleEditor } from "../effectiveDateRange/EffectiveDateRangeRuleEditor";
import { MaxDaysAccessRuleEditor } from "../maxDays/MaxDaysAccessRuleEditor";
import { BlackoutDateRangeAccessRuleEditor } from "../blackoutDateRange/BlackoutDateRangeRuleEditor";
import { BlackoutDayOfWeekAccessRuleEditor } from "../blackoutDayOfWeek/BlackoutDayOfWeekRuleEditor";
import { TimeFormat } from "../../../forms/elements/timeInputUtils";

const DEFAULT_TIME_FORMAT: TimeFormat = "hh:mm:ss A";

export type RuleSection =
  | "effectiveDateRange"
  | "maxDays"
  | "blackoutDateRange"
  | "blackoutDayOfWeek";

type Props = {
  rules: EditableLiftAccessRules;
  sellingSeason: string;
  resources: any;
  dateFormat: string;
  submitCount: number;
  hasChangesSinceSubmit: boolean;
  onRulesChange: (rules: EditableLiftAccessRules) => void;
  onAnyEdit: () => void;
  /**
   * Which rule-type editors to render. Omit to show all four (the default for
   * the single-edit and create flows). The bulk editor passes only the
   * categories the user has chosen to change so unselected categories can't be
   * edited into rules that would be silently ignored on save.
   */
  sections?: RuleSection[];
  /**
   * For the bulk editor: which parts of the effective rule to expose. Dates and
   * time can be replaced independently, so only the chosen part(s) are shown.
   * Omit to show both (single-edit / create flows).
   */
  effectiveShow?: { date: boolean; time: boolean };
  rootCss?: SerializedStyles;
};

export function LiftAccessRulesEditor({
  rules,
  sellingSeason,
  resources,
  dateFormat,
  submitCount,
  hasChangesSinceSubmit,
  onRulesChange,
  onAnyEdit,
  sections,
  effectiveShow,
  rootCss,
}: Props) {
  // Currently assumes only 1 effectiveDateRange
  const effectiveDateRange: EditableEffectiveDateRangeAccessRule | undefined =
    rules.effectiveDateRangeRules.at(0);

  const show = (section: RuleSection) => !sections || sections.includes(section);

  return (
    <Column
      rootCss={css`
        ${COLUMN_SPACING};
        margin-left: 20px;
        height: 565px;
        overflow-y: auto;
        ${rootCss};
      `}
    >
      {show("effectiveDateRange") && (
      <EffectiveDateRangeAccessRuleEditor
        resources={resources}
        season={sellingSeason}
        dateFormat={dateFormat}
        timeFormat={DEFAULT_TIME_FORMAT}
        rules={rules.effectiveDateRangeRules}
        showDate={effectiveShow ? effectiveShow.date : true}
        showTime={effectiveShow ? effectiveShow.time : true}
        submitCount={submitCount}
        hasChangesSinceSubmit={hasChangesSinceSubmit}
        onChange={(updated) => {
          onAnyEdit();
          onRulesChange({
            ...rules,
            effectiveDateRangeRules: updated,
          });
        }}
      />
      )}

      {show("maxDays") && (
      <MaxDaysAccessRuleEditor
        resources={resources}
        rules={rules.maxDaysRules}
        onChange={(updated) => {
          onAnyEdit();
          onRulesChange({
            ...rules,
            maxDaysRules: updated,
          });
        }}
      />
      )}

      {show("blackoutDateRange") && (
      <BlackoutDateRangeAccessRuleEditor
        resources={resources}
        dateFormat={dateFormat}
        timeFormat={DEFAULT_TIME_FORMAT}
        rules={rules.blackoutDateRangeRules}
        effectiveDateRange={effectiveDateRange}
        submitCount={submitCount}
        hasChangesSinceSubmit={hasChangesSinceSubmit}
        onChange={(updated) => {
          onAnyEdit();
          onRulesChange({
            ...rules,
            blackoutDateRangeRules: updated,
          });
        }}
      />
      )}

      {show("blackoutDayOfWeek") && (
      <BlackoutDayOfWeekAccessRuleEditor
        resources={resources}
        timeFormat={DEFAULT_TIME_FORMAT}
        ruleGroups={rules.blackoutDayOfWeekRuleGroups}
        submitCount={submitCount}
        hasChangesSinceSubmit={hasChangesSinceSubmit}
        onAnyEdit={onAnyEdit}
        onChange={(updated) => {
          onAnyEdit();
          onRulesChange({
            ...rules,
            blackoutDayOfWeekRuleGroups: updated,
          });
        }}
      />
      )}
    </Column>
  );
}